/**
 * The GST portal's two ledger balances on the 3B screen (C-05, C-06; FR-E20, FR-E21).
 *
 * The electronic credit ledger is 6.1's opening credit; the electronic cash ledger is what
 * 6.1's cash is paid from before any deposit. **R-E5 (owner, 2026-10-16)**: each comes from
 * the portal's own download for the month (D-121), else the API fetch, else a person typing
 * it off the portal — named by its `source`. The balance the previous month's filed GSTR-3B
 * carried forward is a check, never the source: one that differs is said in words, never
 * absorbed. **Blank is not nil**: a ledger nobody supplied reads "Not entered", never 0.00.
 * What 6.1's cash leaves to deposit is 6.1's own table (`lib/payment.ts`), not this one's.
 *
 * Kept out of the component so the reading is tested without rendering.
 */

export const LEDGER_HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const
export type LedgerHead = (typeof LEDGER_HEADS)[number]

export interface LedgerBalance {
  heads: Record<string, number>
  source?: string
  note?: string | null
  at?: string | null
  carried?: Record<string, number>
  difference?: Record<string, number>
  typed?: Record<string, number>
  /** ND-E5 (D-11): the portal's balance less the typed one, per head, when they differ. */
  typed_difference?: Record<string, number>
}

export type Ledgers = { credit?: LedgerBalance | null; cash?: LedgerBalance | null } | null | undefined

export const LEDGER_LABELS: Record<'credit' | 'cash', string> = {
  credit: 'Electronic credit ledger',
  cash: 'Electronic cash ledger',
}

const HEAD_WORDS: Record<string, string> = {
  igst: 'Integrated tax',
  cgst: 'Central tax',
  sgst: 'State/UT tax',
  cess: 'Cess',
}

function inr(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Where a balance came from, in words — with the typed note when there is one. */
export function ledgerSourceWords(balance: LedgerBalance | null | undefined): string {
  if (!balance) {
    return "Not entered. Upload the GST portal's download of the ledger for this month, or type it below."
  }
  if (balance.source === 'download') return "Read from the GST portal's own download."
  const said =
    balance.source === 'fetched'
      ? 'Returned by the GST portal.'
      : 'Typed from the GST portal by a person.'
  return balance.note ? `${said} Note: ${balance.note}` : said
}

/**
 * The typed balance against the carried one, in a sentence — or null when they agree or
 * there is nothing carried to compare with.
 */
export function differenceWords(balance: LedgerBalance | null | undefined): string | null {
  const diff = balance?.difference
  if (!diff) return null
  const parts = LEDGER_HEADS.filter((h) => (diff[h] ?? 0) !== 0).map((h) => {
    const d = diff[h] ?? 0
    return `${HEAD_WORDS[h]} ₹${inr(Math.abs(d))} ${d > 0 ? 'more' : 'less'}`
  })
  if (parts.length === 0) return null
  const used = balance?.source === 'typed' ? 'typed' : "GST portal's"
  return (
    `The ${used} balance differs from the one carried forward from last month's filed ` +
    `GSTR-3B: ${parts.join(', ')}. The ${used} balance is used; find the cause before filing.`
  )
}

/**
 * ND-E5: the portal's balance against the one typed for the month, in a sentence — or null
 * when only one exists or they agree. The portal's is used; the typed one is never absorbed.
 */
export function typedDifferenceWords(balance: LedgerBalance | null | undefined): string | null {
  const diff = balance?.typed_difference
  if (!diff || balance?.source !== 'fetched') return null
  const parts = LEDGER_HEADS.filter((h) => (diff[h] ?? 0) !== 0).map((h) => {
    const d = diff[h] ?? 0
    return `${HEAD_WORDS[h]} ₹${inr(Math.abs(d))} ${d > 0 ? 'more' : 'less'}`
  })
  if (parts.length === 0) return null
  return (
    `The GST portal's balance differs from the one typed for this month: ${parts.join(', ')}. ` +
    "The portal's balance is used; find the cause before filing."
  )
}
